import { useEffect } from 'react';
import { useFlag } from '@unleash/proxy-client-react';

/**
 * Flag-gated loader for the OpenAI Ads Measurement Pixel.
 *
 * Appends `public/oaiq-loader.js` to <head> exactly once, and ONLY when the
 * Unleash flag `chatgpt-ads-pixel` is ON.
 *
 * WHY A COMPONENT AND NOT A <script> TAG IN index.html
 * ----------------------------------------------------
 * A static tag in index.html would fetch the loader on every page load in
 * every environment the moment it merged. Three things are not ready for that:
 * the CSP does not yet allow `bzrcdn.openai.com` / `bzr.openai.com` (board
 * No9XsUQKiXB0DtgbXV29), the privacy policy still states we run no advertising
 * pixels (board CAxKlF4EIzzitR34epH1), and this lands on shared `dev` where
 * any window can sweep it to prod. Gating on the flag makes the whole feature
 * inert until someone deliberately turns it on: flag OFF means no script
 * element, no network request, no cookie, no `window.oaiq`.
 *
 * `useFlag` returns false when the flag does not exist yet and when the
 * FlagProvider is not mounted (missing token — see src/lib/feature-flags.ts),
 * so the safe state is also the default state.
 *
 * NO CLEANUP ON UNMOUNT — deliberate. Removing the <script> element does not
 * unload the already-executed SDK, and re-appending it on a remount would risk
 * a second `oaiq("init")`. The loader itself is idempotent via
 * `window.__oaiqLoaderRan`, and this component additionally checks for an
 * existing element, so a StrictMode double-mount injects once.
 *
 * Mounted next to <UTMTrackingCapture /> in App.tsx.
 */

const LOADER_SRC = '/oaiq-loader.js';

export function OpenAiPixelLoader() {
  const pixelEnabled = useFlag('chatgpt-ads-pixel');

  useEffect(() => {
    if (!pixelEnabled) return;
    if (typeof document === 'undefined') return;

    // Already injected (remount, or a previous render of this component).
    if (document.querySelector(`script[data-oaiq-loader="1"]`)) return;

    const script = document.createElement('script');
    script.src = LOADER_SRC;
    script.async = true;
    script.dataset.oaiqLoader = '1';
    document.head.appendChild(script);
  }, [pixelEnabled]);

  return null;
}

export default OpenAiPixelLoader;
